import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import CommentList from './Comment';
import { EventActionBar } from './ActionBar';
import UserLink from '../UserLink';
import MediaFrame from './MediaFrame';
import {
    buildExploreLocationPath,
    defaultPfpUrl,
    formatAuthorAffiliation,
    formatRelativeTimestamp,
    formatShortLocation,
} from '../utils';
import PostConnectButton from './PostConnectButton';
import ContentOverflowMenu from './ContentOverflowMenu';
import { FormattedBody } from '../ui/formatted-body';
import { GlobeIcon } from '../../widgets/icons';

/**
 * Event header: author, role · org, timestamp and location, connect.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function EventHeader({ userId, user, event, showContent, setShowContent, toggleShowContent, onDeleted }) {
    const handleToggleShowContent = () => {
        setShowContent(!showContent);
    };

    const relativeTime = formatRelativeTimestamp(event.created_at || event.timestamp);
    const shortLocation = formatShortLocation(event.location);
    const affiliationLine = formatAuthorAffiliation(event);
    const lat = Number(event.latitude);
    const lon = Number(event.longitude);
    const hasMapLocation =
        Boolean(shortLocation) &&
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        !(lat === 0 && lon === 0);
    const explorePath = hasMapLocation
        ? buildExploreLocationPath(lat, lon, shortLocation)
        : "";

    return (
        <div className="flex items-center gap-3">
            <UserLink
                userId={event.author_id}
                isOrg={!!event.author_is_org}
                showProfilePic={true}
                profilePicUrl={event.author_pfp || defaultPfpUrl}
                profilePicSize="h-11 w-11"
                className="flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
                <UserLink
                    userId={event.author_id}
                    isOrg={!!event.author_is_org}
                    className="block truncate text-post font-emphasis text-text-primary hover:text-accent"
                >
                    {event.author_name || "Author"}
                </UserLink>
                {affiliationLine ? (
                    <div className="mt-0.5 truncate text-caption text-text-muted">
                        {affiliationLine}
                    </div>
                ) : null}
                {relativeTime || shortLocation ? (
                    <div className="mt-1 flex min-w-0 items-center gap-1.5 text-text-faint">
                        {relativeTime ? (
                            <span className="shrink-0 font-ui text-meta uppercase tracking-label">
                                {relativeTime}
                            </span>
                        ) : null}
                        {relativeTime ? <GlobeIcon className="shrink-0" /> : null}
                        {shortLocation ? (
                            <>
                                {relativeTime ? <span aria-hidden>·</span> : null}
                                {hasMapLocation ? (
                                    <Link
                                        to={explorePath}
                                        className="min-w-0 truncate text-meta text-text-faint hover:text-accent hover:underline"
                                    >
                                        {shortLocation}
                                    </Link>
                                ) : (
                                    <span className="min-w-0 truncate text-meta">
                                        {shortLocation}
                                    </span>
                                )}
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
            {!toggleShowContent ? (
                <PostConnectButton userId={userId} user={user} item={event} />
            ) : null}
            {toggleShowContent ? (
                <button
                    type="button"
                    className="text-compact text-accent hover:text-accent-hover"
                    onClick={handleToggleShowContent}
                >
                    {showContent ? "Hide Content" : "Show Content"}
                </button>
            ) : null}
            <ContentOverflowMenu
                userId={userId}
                user={user}
                item={event}
                contentType="event"
                onDeleted={onDeleted}
            />
            </div>
        </div>
    );
}

// Event component displays event details and attendees
function Event({ userId, user, event, hideContent, focusCommentId = null, onDeleted }) {
    // State to toggle content visibility
    const [showContent, setShowContent] = useState(!hideContent);
    const [pendingComment, setPendingComment] = useState(null);
    const [removed, setRemoved] = useState(false);

    const handleDeleted = () => {
        setRemoved(true);
        onDeleted?.();
    };

    if (removed) {
        return null;
    }

    return (
        <div className="event min-w-0 overflow-x-hidden rounded-card border border-ui-border bg-ui-card p-5 shadow-shell-sm">
            <EventHeader
                userId={userId}
                user={user}
                event={event}
                showContent={showContent}
                setShowContent={setShowContent}
                toggleShowContent={hideContent}
                onDeleted={handleDeleted}
            />
            <div className="event-body mt-3.5">
                {(event.start_time || event.end_time) ? (
                    <div className="mb-2 text-caption text-text-muted">
                        {event.start_time ? (
                            <span>Start: {new Date(event.start_time).toLocaleString()}</span>
                        ) : null}
                        {event.end_time ? (
                            <span>
                                {event.start_time ? " · " : null}
                                End: {new Date(event.end_time).toLocaleString()}
                            </span>
                        ) : null}
                    </div>
                ) : null}
                <FormattedBody as="p" className="text-post text-text-primary" mentions={event.mentions}>{event.description}</FormattedBody>
                {showContent && (
                    <MediaFrame post={event} />
                )}
            </div>
            {showContent && (
                <EventActionBar
                    userId={userId}
                    user={user}
                    event={event}
                    onCommentCreated={setPendingComment}
                />
            )}
            {showContent && (
                <CommentList
                    userId={userId}
                    user={user}
                    post={event}
                    contentType="event"
                    pendingComment={pendingComment}
                    onPendingCommentConsumed={() => setPendingComment(null)}
                    focusCommentId={focusCommentId}
                />
            )}
        </div>
    );
}

export default Event;
